/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactNode } from 'react';
import { Outlet, type Location, useLocation, useNavigation } from 'react-router';

export type PendingNavigationContext = {
    currentLocation: Location;
    nextLocation: Location;
    navigation: ReturnType<typeof useNavigation>;
};

/** A synchronous, side-effect-free selector for pending navigation UI. */
export type PendingNavigationResolver = (context: PendingNavigationContext) => ReactNode | undefined;

/**
 * Lets the current layout render destination-aware feedback while the next
 * route is loading. Returning `undefined` from the resolver keeps the current
 * outlet mounted; any other React node becomes the pending UI.
 */
export default function PendingNavigationOutlet({ resolvePending }: { resolvePending: PendingNavigationResolver }) {
    const currentLocation = useLocation();
    const navigation = useNavigation();

    if (navigation.state === 'loading' && navigation.location) {
        const pendingElement = resolvePending({
            currentLocation,
            nextLocation: navigation.location,
            navigation,
        });

        if (pendingElement !== undefined) {
            return pendingElement;
        }
    }

    return <Outlet />;
}
